import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { SearchLg, TextInput } from "@opendate-app/setlist";
import client from "../../../../shared/api/client";
import useLocalSearchShortcut from "../../hooks/useLocalSearchShortcut";

const DEBOUNCE_MS = 200;
const MAX_QUERY_LENGTH = 100;
const isMacLike = () => /Mac|iPhone|iPad|iPod/.test(navigator.platform || "");

let instanceCount = 0;

function SectionNavigationSearch({ url, placeholder = "Search…", sectionTitle }) {
  const [inputId] = useState(() => {
    instanceCount += 1;
    return `section-navigation-search-${instanceCount}`;
  });
  const listboxId = `${inputId}-listbox`;
  const containerRef = useRef(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [fetchedTerm, setFetchedTerm] = useState("");
  const [failed, setFailed] = useState(false);
  const [active, setActive] = useState(0);
  const term = query.trim();

  const findInput = useCallback(() => containerRef.current?.querySelector("input"), []);
  const shortcut = useLocalSearchShortcut(findInput);
  const shortcutLabel = shortcut && !isMacLike() ? "Ctrl K" : shortcut;

  useEffect(() => {
    setFailed(false);
    if (!term) {
      setGroups([]);
      setFetchedTerm("");
      return undefined;
    }
    let live = true;
    const timer = setTimeout(() => {
      client.get(url, { params: { q: term } })
        .then(({ data }) => {
          if (!live) return;
          if (!data || !Array.isArray(data.groups)) throw new Error("unexpected response");
          setGroups(data.groups);
          setFetchedTerm(term);
        })
        .catch(() => {
          if (!live) return;
          setGroups([]);
          setFetchedTerm("");
          setFailed(true);
        });
    }, DEBOUNCE_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [term, url]);

  useEffect(() => { setActive(0); }, [groups]);

  const visibleGroups = groups.filter((group) => group.items.length > 0);
  const rows = visibleGroups.flatMap((group) => group.items.map((item) => ({ group: group.key, item })));
  const stale = fetchedTerm !== term;
  const showPanel = open && term.length > 0;

  const close = () => setOpen(false);

  const resultsStatus = () => {
    if (!showPanel || failed) return "";
    if (stale) return "Searching…";
    if (rows.length === 0) return `Nothing matches “${term}”.`;
    return rows.length === 1 ? "1 result" : `${rows.length} results`;
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      if (showPanel) close();
      else setQuery("");
      return;
    }
    if (!showPanel) {
      if (e.key === "ArrowDown" && term) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((index) => Math.min(index + 1, Math.max(rows.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((index) => Math.max(index - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (stale || !rows[active]) return;
      if (e.metaKey || e.ctrlKey) window.open(rows[active].item.href, "_blank", "noopener,noreferrer");
      else window.location.href = rows[active].item.href;
    }
  };

  const onBlur = (e) => {
    if (containerRef.current && !containerRef.current.contains(e.relatedTarget)) close();
  };

  let cursor = -1;

  return (
    <div className="section-navigation-search" ref={containerRef} onBlur={onBlur}>
      <TextInput
        id={inputId}
        size="sm"
        type="text"
        autoComplete="off"
        spellCheck={false}
        placeholder={placeholder}
        aria-label={`Search ${sectionTitle.toLowerCase()}`}
        role="combobox"
        aria-expanded={showPanel}
        aria-controls={showPanel ? listboxId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={showPanel && !stale && rows[active] ? `${inputId}-option-${active}` : undefined}
        leadingIcon={SearchLg}
        trailingText={shortcutLabel && !query ? <kbd className="section-navigation-search__kbd">{shortcutLabel}</kbd> : undefined}
        value={query}
        maxLength={MAX_QUERY_LENGTH}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      <span className="sr-only" role="status">{resultsStatus()}</span>
      {showPanel && (
        <div className="section-navigation-search__panel" onMouseDown={(e) => e.preventDefault()}>
          {failed && <div className="section-navigation-search__status" role="alert">Search failed. Reload the page and try again.</div>}
          {!failed && stale && <div className="section-navigation-search__status">Searching…</div>}
          {!failed && !stale && rows.length === 0 && (
            <div className="section-navigation-search__status">Nothing matches “{term}”.</div>
          )}
          <div id={listboxId} role="listbox" aria-label="Search results">
            {!stale && visibleGroups.map((group) => (
              <div key={group.key} role="group" aria-label={group.label}>
                <div className="section-navigation-search__group" role="presentation">{group.label}</div>
                {group.items.map((item) => {
                  cursor += 1;
                  const index = cursor;
                  const isActive = index === active;
                  return (
                    <a
                      key={`${group.key}-${item.id}`}
                      id={`${inputId}-option-${index}`}
                      href={item.href}
                      role="option"
                      tabIndex={-1}
                      aria-selected={isActive}
                      className={"section-navigation-search__option" + (isActive ? " is-active" : "")}
                      onMouseEnter={() => setActive(index)}
                    >
                      <span className="section-navigation-search__option-label">{item.label}</span>
                      {item.detail && <span className="section-navigation-search__option-detail">{item.detail}</span>}
                    </a>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

SectionNavigationSearch.propTypes = {
  url: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  sectionTitle: PropTypes.string.isRequired,
};

export default SectionNavigationSearch;
